import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, Send, Inbox, Heart, CreditCard, Calendar, BarChart3 } from 'lucide-react';
import { useMatchAnalytics } from '@/hooks/useMatchAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

export const MatchAnalytics = () => {
  const { t } = useTranslation();
  const [timeframe, setTimeframe] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const { analytics, isLoading } = useMatchAnalytics(timeframe);

  if (isLoading) {
    return (
      <div className="container max-w-6xl mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="container max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="h-8 w-8 text-pink-500" />
            {t('matchAnalytics.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('matchAnalytics.subtitle')}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant={timeframe === '7d' ? 'default' : 'outline'}
            onClick={() => setTimeframe('7d')}
            size="sm"
          >
            {t('matchAnalytics.timeframes.7d')}
          </Button>
          <Button
            variant={timeframe === '30d' ? 'default' : 'outline'}
            onClick={() => setTimeframe('30d')}
            size="sm"
          >
            {t('matchAnalytics.timeframes.30d')}
          </Button>
          <Button
            variant={timeframe === '90d' ? 'default' : 'outline'}
            onClick={() => setTimeframe('90d')}
            size="sm"
          >
            {t('matchAnalytics.timeframes.90d')}
          </Button>
          <Button
            variant={timeframe === 'all' ? 'default' : 'outline'}
            onClick={() => setTimeframe('all')}
            size="sm"
          >
            {t('matchAnalytics.timeframes.all')}
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('matchAnalytics.overview.sentRequests')}</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.overview.total_sent}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('matchAnalytics.overview.receivedRequests')}</CardTitle>
            <Inbox className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.overview.total_received}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('matchAnalytics.overview.activeMatches')}</CardTitle>
            <Heart className="h-4 w-4 text-pink-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-pink-500">
              {analytics.overview.total_active_matches}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('matchAnalytics.overview.successRate')}</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {analytics.overview.acceptance_rate}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Stats */}
      <Tabs defaultValue="requests" className="space-y-4">
        <TabsList>
          <TabsTrigger value="requests">{t('matchAnalytics.statistics.requestsTab')}</TabsTrigger>
          <TabsTrigger value="matches">{t('matchAnalytics.statistics.matchesTab')}</TabsTrigger>
          <TabsTrigger value="credits">{t('matchAnalytics.statistics.creditsTab')}</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sent Requests */}
            <Card>
              <CardHeader>
                <CardTitle>Sent Requests</CardTitle>
                <CardDescription>Requests you've sent to others</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm">Pending</span>
                  <Badge variant="outline">{analytics.sent_requests.pending}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Accepted</span>
                  <Badge className="bg-green-500">{analytics.sent_requests.accepted}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Rejected</span>
                  <Badge variant="destructive">{analytics.sent_requests.rejected}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Expired</span>
                  <Badge variant="secondary">{analytics.sent_requests.expired}</Badge>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-xs font-semibold mb-2">By Type:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Playdate: {analytics.sent_requests.by_type.playdate}</div>
                    <div>Friendship: {analytics.sent_requests.by_type.friendship}</div>
                    <div>Breeding: {analytics.sent_requests.by_type.breeding}</div>
                    <div>Adoption: {analytics.sent_requests.by_type.adoption}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Received Requests */}
            <Card>
              <CardHeader>
                <CardTitle>Received Requests</CardTitle>
                <CardDescription>Requests from other users</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm">Pending</span>
                  <Badge variant="outline">{analytics.received_requests.pending}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Accepted</span>
                  <Badge className="bg-green-500">{analytics.received_requests.accepted}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Rejected</span>
                  <Badge variant="destructive">{analytics.received_requests.rejected}</Badge>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-xs font-semibold mb-2">By Type:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Playdate: {analytics.received_requests.by_type.playdate}</div>
                    <div>Friendship: {analytics.received_requests.by_type.friendship}</div>
                    <div>Breeding: {analytics.received_requests.by_type.breeding}</div>
                    <div>Adoption: {analytics.received_requests.by_type.adoption}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="matches" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Active Matches</CardTitle>
              <CardDescription>Your current active connections</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span>Total Active</span>
                <Badge className="bg-pink-500">{analytics.active_matches.total}</Badge>
              </div>
              <div className="flex justify-between">
                <span>With Chat</span>
                <Badge variant="outline">{analytics.active_matches.with_chat}</Badge>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm font-semibold mb-3">By Type:</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-sm">
                    <div className="text-muted-foreground">Playdate</div>
                    <div className="text-lg font-bold">{analytics.active_matches.by_type.playdate}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-muted-foreground">Friendship</div>
                    <div className="text-lg font-bold">{analytics.active_matches.by_type.friendship}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-muted-foreground">Breeding</div>
                    <div className="text-lg font-bold">{analytics.active_matches.by_type.breeding}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-muted-foreground">Adoption</div>
                    <div className="text-lg font-bold">{analytics.active_matches.by_type.adoption}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="credits" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Credit Usage
              </CardTitle>
              <CardDescription>Your credit spending analytics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span>{t('analytics.creditsUsage.totalPurchased')}</span>
                <span className="text-2xl font-bold text-green-500">
                  {analytics.credits.total_purchased}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>{t('analytics.creditsUsage.totalSpent')}</span>
                <span className="text-2xl font-bold text-orange-500">
                  {analytics.credits.total_spent}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>{t('analytics.creditsUsage.onRegular')}</span>
                <Badge variant="outline">{analytics.credits.spent_on_matches}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span>{t('analytics.creditsUsage.onSuper')}</span>
                <Badge className="bg-purple-500">{analytics.credits.spent_on_super_matches}</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
